/**
 * Read-only preview dialog for a glossary term.
 * Receives the term to preview and optional onEdit callback.
 */
import React from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Eye, Edit, Languages } from 'lucide-react';

const GlossaryPreviewDialog = ({ open, onOpenChange, term, onEdit }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Eye className="w-5 h-5 text-blue-600" />
          Begriffsvorschau
        </DialogTitle>
      </DialogHeader>
      {term && (
        <div className="space-y-4">
          <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800">
            <h3 className="text-xl font-bold text-indigo-800 dark:text-indigo-200 mb-2">
              {term.term}
            </h3>
            <p className="text-slate-700 dark:text-slate-300">
              {term.master_definition || term.definition}
            </p>
            {term.primary_source && (
              <p className="text-sm text-indigo-600 dark:text-indigo-400 mt-2">
                Quelle: {term.primary_source}
              </p>
            )}
          </div>

          {term.context_definitions && Object.keys(term.context_definitions).length > 0 && (
            <div>
              <h4 className="font-semibold mb-2">Kontext-spezifische Definitionen:</h4>
              <div className="space-y-2">
                {Object.entries(term.context_definitions).map(([ctx, data]) => (
                  <div
                    key={ctx}
                    className="p-3 bg-slate-100 dark:bg-slate-800 rounded border-l-4 border-l-blue-500"
                  >
                    <Badge className="mb-1">{ctx}</Badge>
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      {data.definition}
                    </p>
                    {data.reference && (
                      <p className="text-xs text-slate-400 mt-1">Ref: {data.reference}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <Badge variant="outline">{term.category}</Badge>
            {term.tags?.map((tag) => (
              <Badge key={`${term.term_id || term.term}-${tag}`} variant="secondary">
                {tag}
              </Badge>
            ))}
            {term.do_not_translate && (
              <Badge variant="destructive" className="bg-orange-500">
                <Languages className="w-3 h-3 mr-1" />
                Nicht übersetzen
              </Badge>
            )}
          </div>
        </div>
      )}
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Schließen
        </Button>
        {onEdit && (
          <Button
            onClick={() => {
              onOpenChange(false);
              onEdit(term);
            }}
          >
            <Edit className="w-4 h-4 mr-2" />
            Bearbeiten
          </Button>
        )}
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export default GlossaryPreviewDialog;
